import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import { Address } from 'api/ApplicationApi';
import { updateBorrower, getBorrowerByToken } from 'thunks';
import { CurrentOwnershipStatus } from 'enums/ApplicationsVariables';

import { BorrowerDto, BorrowerLockReason } from '../api/BorrowerApi';

export interface PossessionCheck {
  linkClicked?: boolean;
  phoneMatch?: string;
  ipAddressMatch?: boolean;
}

export interface BorrowerState {
  borrowerId: string | null;
  firstName: string;
  prefilledFirstName: string;
  lastName: string;
  prefilledLastName: string;
  dateOfBirth: string;
  email: string;
  streetName: string;
  streetNumber: string;
  prefilledStreetAddress: string;
  city: string;
  prefilledCity: string;
  state: string;
  prefilledState: string;
  zip: string;
  prefilledZipOrPostalCode: string;
  county: string;
  phone: string;
  personalIdNumber: string;
  prefilledSsn: string;
  permanentResident: boolean;
  isLocked: boolean;
  lockReason: BorrowerLockReason | null;
  moveInDate: string;
  confirmEmail?: string | null;
  eligible: boolean;
  borrowerFraud?: boolean | null;
  possessionCheck: PossessionCheck;
  requiredInformationComplete?: boolean;
  isHomeImprovementSameAsPrimaryAddress?: boolean | null;
  homeImprovementAddress: Address;
  previousAddress: Address;
  purchaseDate?: string | null;
  ownershipStatus: CurrentOwnershipStatus | null;
  previousMoveInDate: string;
  borrowerInformationRequested: boolean;
  consentAgreed: boolean;
  fullConsentAgreed: boolean;
}

const initialState: BorrowerState = {
  borrowerId: null,
  firstName: '',
  prefilledFirstName: '',
  lastName: '',
  prefilledLastName: '',
  dateOfBirth: '',
  email: '',
  confirmEmail: '',
  streetName: '',
  streetNumber: '',
  prefilledStreetAddress: '',
  city: '',
  prefilledCity: '',
  state: '',
  prefilledState: '',
  zip: '',
  prefilledZipOrPostalCode: '',
  county: '',
  phone: '',
  personalIdNumber: '',
  prefilledSsn: '',
  permanentResident: false,
  isLocked: false,
  lockReason: null,
  moveInDate: '',
  eligible: false,
  possessionCheck: {},
  requiredInformationComplete: false,
  isHomeImprovementSameAsPrimaryAddress: null,
  homeImprovementAddress: {
    street_name: '',
    city: '',
    state_or_province: '',
    zip_or_postal_code: '',
  },
  ownershipStatus: null,
  previousAddress: {
    street_name: '',
    city: '',
    state_or_province: '',
    zip_or_postal_code: '',
  },
  previousMoveInDate: '',
  borrowerInformationRequested: false,
  fullConsentAgreed: false,
  consentAgreed: false,
};

const borrower = createSlice({
  name: 'borrower',
  initialState: initialState,
  reducers: {
    setBorrower: (state: BorrowerState, { payload }: PayloadAction<Partial<BorrowerState>>) => {
      Object.assign(state, payload);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(updateBorrower.fulfilled, getBorrowerByToken.fulfilled),
      (state, action: PayloadAction<BorrowerDto>) => {
        const { variables } = action.payload;
        const newBorrowerData = {
          borrowerId: action.payload.id,
          isLocked: action.payload.locked,
          lockReason: action.payload.lockReason,
          email: variables.borrower_email,
          phone: variables.borrower_phone,
          dateOfBirth: variables.borrower_date_of_birth,
          firstName: variables.borrower_first_name,
          lastName: variables.borrower_last_name,
          streetName: variables.borrower_home_address.street_name,
          streetNumber: variables.borrower_home_address.street_number,
          city: variables.borrower_home_address.city,
          state: variables.borrower_home_address.state_or_province,
          zip: variables.borrower_home_address.zip_or_postal_code,
          county: variables.borrower_county,
          personalIdNumber: variables.borrower_personal_id_number,
          borrowerFraud: variables.borrower_fraud,
        };

        Object.assign(state, newBorrowerData);
      },
    );
  },
});

export const { setBorrower } = borrower.actions;

export default borrower.reducer;
